import { useEffect, useMemo, useRef } from "react";
let layerCounter = 0;
/**
 * Layer Component that MUST be nested in a Source component. Creates new layers and
 * changes LayerProperties, when new props for the layer are passed.
 */
export const Layer = (props) => {
    const map = props.map;
    const id = useMemo(() => props.id || `jsx-layer-${layerCounter++}`, [props.source]);
    const prevProps = useRef(props);
    const { layout = {}, paint = {}, filter, level, minzoom, maxzoom } = props;
    const addLayer = () => {
        const layerProps = { ...props, id };
        if (!map.getSource(props.source))
            return;
        delete layerProps.beforeId;
        map.removeLayer(layerProps.id);
        if (props.beforeId && map.getLayer(props.beforeId)) {
            map.addLayer(layerProps, props.beforeId);
        }
        else {
            map.addLayer(layerProps);
            if (props.logWarning) {
                console.warn(`The given beforeId for Layer ${id} does not yet exist on this map`);
            }
        }
    };
    const removeLayer = () => {
        map.removeLeveledLevelId(id);
        map.removeLayer(id);
    };
    const updateLayout = () => {
        if (!map.getLayer(id))
            return;
        const prevLayout = prevProps.current.layout || {};
        for (const key in layout) {
            map.setLayoutProperty(id, key, layout[key]);
        }
        for (const key in prevLayout) {
            if (!layout.hasOwnProperty(key)) {
                map.setLayoutProperty(id, key, undefined);
            }
        }
    };
    const updatePaint = () => {
        if (!map.getLayer(id))
            return;
        const prevPaint = prevProps.current.paint || {};
        for (const key in paint) {
            map.setPaintProperty(id, key, paint[key]);
        }
        for (const key in prevPaint) {
            if (!paint.hasOwnProperty(key)) {
                map.setPaintProperty(id, key, undefined);
            }
        }
    };
    const updateFilter = () => {
        const updateRasterImageLevel = () => {
            // This is not meant for GeoJSON Raster Layers, but
            // for the RasterImages that the user gives us
            if (props.rasterLevel != null) {
                if (props.rasterLevel != level) {
                    map.setLayoutProperty(id, "visibility", "none");
                }
                else {
                    map.setLayoutProperty(id, "visibility", "visible");
                }
            }
        };
        if (!map.getLayer(id))
            return;
        if (map.getLayer(id).type === "raster") {
            updateRasterImageLevel();
        }
        const allFilters = ["all"];
        // in case a level is given, we'll add a level filter
        if (level != null) {
            allFilters.push(["==", ["get", "level"], level]);
        }
        if (filter != null) {
            allFilters.push(filter);
        }
        map.setFilter(id, allFilters);
        if (minzoom !== prevProps.current.minzoom ||
            maxzoom !== prevProps.current.maxzoom) {
            map.setLayerZoomRange(id, minzoom !== null && minzoom !== void 0 ? minzoom : 0, maxzoom !== null && maxzoom !== void 0 ? maxzoom : 22);
        }
    };
    const moveLayer = () => {
        if (!map.getLayer(id))
            return;
        if (props.beforeId && map.getLayer(props.beforeId)) {
            map.moveLayer(id, props.beforeId);
        }
    };
    useEffect(addLayer, [props.source, props.beforeId, props.id]);
    useEffect(updateFilter, [filter, minzoom, maxzoom, level]);
    useEffect(updatePaint, [paint]);
    useEffect(updateLayout, [layout]);
    useEffect(moveLayer, [props.beforeId]);
    useListeners(props, id);
    // unmouting
    useEffect(() => removeLayer, []);
    return null;
};
function useListeners(props, id) {
    var _a;
    const map = props.map;
    const prevListeners = useRef([]);
    /** Wenn neue listeners kommen müssen die alten vorher entfernt werden */
    const removePrevListeners = () => {
        prevListeners.current.forEach((listener) => {
            map.off(listener.type, id, listener.listener);
            // Akzeptiert nur MapLayerMouseEvent und MapLayerTouchEvent
            // Akzeptiert nicht MapMouseEvent. Woher kommt es?
            // Name: ev ist inkompatibel.
        });
    };
    const setListeners = () => {
        var _a;
        removePrevListeners();
        // listeners müssen kopiert werden, da es sonst nur eine referenz
        // zu den props ist und die alten Listener dadurch nicht erlöst werden können
        prevListeners.current = props.listeners ? [...props.listeners] : [];
        (_a = prevListeners.current) === null || _a === void 0 ? void 0 : _a.forEach((listener) => {
            map.on(listener.type, id, listener.listener);
        });
    };
    useEffect(setListeners, [props.listeners, (_a = props.listeners) === null || _a === void 0 ? void 0 : _a.length]);
}
